import { Component, OnInit, AfterViewInit, Output, EventEmitter, Input, ViewChild, ElementRef } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { ToastrService } from "ngx-toastr";

interface PhotoFile {
  id: string;
  name: string;
  size: number;
  type: string;
  url?: string;
  preview?: string;
  progress?: number;
}

interface UploadingFile {
  name: string;
  progress: number;
}

@Component({
  selector: "common-machine-create-wizard-photos",
  templateUrl: "./machine-create-wizard-photos.component.html",
  styleUrls: ["./machine-create-wizard-photos.component.scss"],
})
export class MachineCreateWizardPhotosComponent implements OnInit, AfterViewInit {
  @Input() machineId: string;
  @Output() photosSaved = new EventEmitter<PhotoFile[]>();
  @ViewChild("fileInput") fileInput: ElementRef;

  // Wizard step state
  currentStep: "private" | "listing" = "private";

  // Form data
  photos: PhotoFile[] = [];
  uploadingFiles: UploadingFile[] = [];
  loading: boolean = false;
  validationErrors: string[] = [];

  // Photo upload tracking
  listingPhotosUploaded: number = 0;

  // Machine data
  actualMachineId: string = "";
  partitionKey: string = "";

  // Drag and drop state
  isDragOver: boolean = false;
  previewPhotoUrl: string = "";

  constructor(private router: Router, private route: ActivatedRoute, private toastrService: ToastrService) {}

  ngOnInit(): void {
    // Get machine ID and partition key from route
    this.actualMachineId = this.route.snapshot.params["id"] || this.machineId;
    this.partitionKey = this.route.snapshot.queryParams["partitionKey"] || "temp";
  }

  ngAfterViewInit(): void {
    // Allow some time for child components to load their data
    // Then check if we should enable the button based on existing photos
    setTimeout(() => {
      this.checkExistingPhotos();
    }, 1000);
  }

  private checkExistingPhotos(): void {
    // This method will be called after view init to check if existing photos make the button available
    // The button will re-evaluate its disabled state through change detection
  }

  // Helper method to get inventory item for image components
  getInventoryItem() {
    return {
      id: this.actualMachineId,
      accountId: this.partitionKey,
    };
  }

  // File upload methods
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files) {
      this.processFiles(Array.from(files));
    }
  }

  onFileSelected(event: any): void {
    const files = event.target.files;
    if (files) {
      this.processFiles(Array.from(files));
    }
  }

  private processFiles(files: File[]): void {
    const validFiles = files.filter(file => {
      // Check file type
      if (!file.type.startsWith("image/")) {
        this.toastrService.error(`${file.name} is not an image file.`);
        return false;
      }

      // Check file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        this.toastrService.error(`${file.name} is too large. Maximum size is 10MB.`);
        return false;
      }

      return true;
    });

    validFiles.forEach(file => {
      this.addPhoto(file);
    });
  }

  private addPhoto(file: File): void {
    const photo: PhotoFile = {
      id: this.generateId(),
      name: file.name,
      size: file.size,
      type: file.type,
    };

    // Create preview
    const reader = new FileReader();
    reader.onload = (e: any) => {
      photo.preview = e.target.result;
    };
    reader.readAsDataURL(file);

    this.photos.push(photo);
    this.simulateUpload(photo);
  }

  private generateId(): string {
    return Math.random().toString(36).substr(2, 9);
  }

  private simulateUpload(photo: PhotoFile): void {
    // Add to uploading files
    const uploadingFile: UploadingFile = {
      name: photo.name,
      progress: 0,
    };
    this.uploadingFiles.push(uploadingFile);

    // Simulate upload progress
    const interval = setInterval(() => {
      uploadingFile.progress += Math.random() * 20;
      if (uploadingFile.progress >= 100) {
        uploadingFile.progress = 100;
        clearInterval(interval);

        // Remove from uploading files after a delay
        setTimeout(() => {
          const index = this.uploadingFiles.findIndex(f => f.name === photo.name);
          if (index > -1) {
            this.uploadingFiles.splice(index, 1);
          }
        }, 1000);
      }
    }, 200);
  }

  // Photo management methods
  removePhoto(index: number): void {
    this.photos.splice(index, 1);
  }

  movePhoto(index: number, direction: number): void {
    const newIndex = index + direction;
    if (newIndex >= 0 && newIndex < this.photos.length) {
      const photo = this.photos.splice(index, 1)[0];
      this.photos.splice(newIndex, 0, photo);
    }
  }

  previewPhoto(index: number): void {
    const photo = this.photos[index];
    this.previewPhotoUrl = photo.preview || photo.url || "";

    // Show modal (you'll need to implement modal functionality)
    // For now, we'll just log the photo
    console.log("Preview photo:", photo);
  }

  // Wizard step methods — private (verification) photos may be added later; always allow continuing.
  proceedToListingPhotos(): void {
    this.currentStep = "listing";
    window.scrollTo(0, 0);
  }

  goBackToPrivatePhotos(): void {
    this.currentStep = "private";
    window.scrollTo(0, 0);
  }

  onListingImageUploaded(count: number): void {
    this.listingPhotosUploaded = count;
  }

  // Validation methods
  canProceed(): boolean {
    this.validationErrors = [];

    if (this.currentStep === "listing" && this.listingPhotosUploaded < 1) {
      this.validationErrors.push("Please upload at least one listing photo of your machine.");
    }

    return this.validationErrors.length === 0;
  }

  // Navigation methods
  onBack(): void {
    const currentUrl = this.router.url;
    const baseUrl = currentUrl.split("/").slice(0, -1).join("/");
    this.router.navigate([baseUrl, "attachments"], {
      queryParams: { partitionKey: this.route.snapshot.queryParams["partitionKey"] },
    });
  }

  onFinish(): void {
    if (this.loading || !this.canProceed()) {
      return;
    }

    this.loading = true;

    // Emit the photos
    this.photosSaved.emit(this.photos);

    // Show success message
    this.toastrService.success("Machine listing created successfully!", null, {
      positionClass: "toast-top-center",
    });

    // Navigate to machine details page
    setTimeout(() => {
      this.router.navigate(["/owner/fleet", this.actualMachineId, "details"], {
        queryParams: { partitionKey: this.partitionKey },
      });
    }, 2000);

    this.loading = false;
  }
}
